import React, { useState } from 'react';
import {
  BirdzModal,
  BirdzModalActions,
  BirdzModalContent,
  BirdzModalTitle,
  themeColors,
  useGlobalContext
} from '@applications-terrains/birdz-react-library';
import { Box, Button, Link, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ImportTemplatesModal from './ImportTemplatesModal';
import CopyPasteTab from './CopyPasteTab';
import FileTab from './FileTab';
import UnitEntryTab from './UnitEntryTab';
import { ImportKind } from './ImportKindToggle';
import { PdsImportState } from './usePdsImport';

/** Onglets de la modale, dans l'ordre de la maquette. */
type SourceTab = 'file' | 'copyPaste' | 'unitEntry';

const SOURCE_TABS: SourceTab[] = ['file', 'copyPaste', 'unitEntry'];

type AddServicePointsModalProps = {
  open: boolean;
  onClose: () => void;
  /** Porté par l'écran : le bilan et l'erreur doivent survivre à la fermeture. */
  importState: PdsImportState;
};

export default function AddServicePointsModal({
  open,
  onClose,
  importState
}: AddServicePointsModalProps) {
  const { t } = useTranslation();
  // La maquette montre un titre orange, mais c'est la couleur d'accentuation du thème
  // choisi par l'utilisateur : `themeColors` couvre les six thèmes, la valeur est
  // donc toujours définie.
  const { theme } = useGlobalContext();
  const [tab, setTab] = useState<SourceTab>('file');
  // Le type d'ajout est commun aux onglets : on ne le repose pas en changeant de source.
  const [kind, setKind] = useState<ImportKind>('maintenance');
  const [templatesOpen, setTemplatesOpen] = useState(false);

  /**
   * La modale se ferme dès le lancement de l'import, pas à sa fin : le voile de
   * chargement vit au niveau de l'écran, et la modale le masquerait pendant toute la
   * durée de la requête.
   *
   * Corollaire : les onglets se démontent aussitôt, donc plus besoin qu'ils nettoient
   * leur saisie — un remontage repart d'un état vide.
   */
  const importAndClose: PdsImportState['importPds'] = (input) => {
    importState.importPds(input);
    onClose();
  };

  const tabImportState: PdsImportState = { ...importState, importPds: importAndClose };

  return (
    <>
      <BirdzModal open={open} onClose={onClose} maxWidth="md">
        <BirdzModalTitle onClose={onClose}>
          {/*
            Les types publiés de BirdzModalTitle n'exposent pas `sx` : on colore le
            libellé plutôt que le conteneur. `component="span"` évite d'imbriquer
            un titre dans le h2 du DialogTitle.
          */}
          <Typography
            component="span"
            sx={{ color: themeColors[theme], fontWeight: 700, fontSize: 'inherit' }}
          >
            {t('interventionCreation.addServicePoints.title')}
          </Typography>
        </BirdzModalTitle>
        <BirdzModalContent>
          <Stack spacing={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Les deux flex:1 de part et d'autre centrent le groupe d'onglets. */}
              <Box sx={{ flex: 1 }} />
              <Tabs value={tab} onChange={(_, value: SourceTab) => setTab(value)}>
                {SOURCE_TABS.map((source) => (
                  <Tab
                    key={source}
                    value={source}
                    label={t(`interventionCreation.addServicePoints.tabs.${source}`)}
                  />
                ))}
              </Tabs>
              {/* Pas de modèle de fichier à proposer en saisie unitaire. */}
              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                {tab !== 'unitEntry' && (
                  <Link component="button" type="button" onClick={() => setTemplatesOpen(true)}>
                    {t('interventionCreation.addServicePoints.seeTemplates')}
                  </Link>
                )}
              </Box>
            </Box>

            {tab === 'file' && (
              <FileTab kind={kind} onKindChange={setKind} importState={tabImportState} />
            )}
            {tab === 'copyPaste' && (
              <CopyPasteTab kind={kind} onKindChange={setKind} importState={tabImportState} />
            )}
            {tab === 'unitEntry' && <UnitEntryTab />}
          </Stack>
        </BirdzModalContent>
        <BirdzModalActions>
          <Button variant="outlined" onClick={onClose}>
            {t('interventionCreation.addServicePoints.close')}
          </Button>
        </BirdzModalActions>
      </BirdzModal>

      <ImportTemplatesModal open={templatesOpen} onClose={() => setTemplatesOpen(false)} />
    </>
  );
}
